import React, { useState, useRef, useEffect } from "react";
import CameraController from "./CameraController";
import CitizenDocumentChooser from "./CitizenDocumentChooser";
import NonCitizenDocumentChooser from "./NonCitizenDocumentChooser";
import PassportFlow from "./PassportFlow";
import DriverLicenseFlow from "./DriverLicenseFlow";
import GreenCardFlow from "./GreenCardFlow";
import EadFlow from "./EadFlow";
import SsnFlow from "./SsnFlow";
import {
    updateProfilePicture,
    uploadDriverLicense,
    updateDriverLicenseInformation,
    uploadPassportFile,
    uploadBirthCertificate,
    uploadGreenCard,
    uploadEADCard,
    updatePassportInformation,
    updateGreenCardInformation,
    updateEADInformation,
    uploadSSNFile,
    updateSSNInformation
} from "helpers/fakebackend_helper";
import StepCompleteActionButton from "./StepCompleteActionButton";

const DynamicCameraOcrController = ({ step, token, email, onNext }) => {
    const [phase, setPhase] = useState("choose");
    const [subPhase, setSubPhase] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState("");
    const [rawText, setRawText] = useState("");
    const [fields, setFields] = useState({});

    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const handleTakePhoto = (img) => {
        setPreview(img);
    };

    const handleRetake = () => {
        setPreview("");
        setRawText("");

        // Force re-render of CameraController to restart camera
        setPhase("camera_reload");

        setTimeout(() => {
            setPhase("camera");
        }, 10);
    };

    const handleChoose = ({ subPhase, nextPhase, reset }) => {
        setSubPhase(subPhase);
        setPhase(nextPhase);

        if (reset) {
            setPreview("");
            setRawText("");
        }
    };

    const handleUpload = async (blob) => {
        try {
            setLoading(true);
            setError("");

            const file = new File([blob], "image.jpg", { type: "image/jpeg" });
            const form = new FormData();
            form.append("file", file);
            form.append("email", email);

            let r;

            if (subPhase === "passport") {
                r = await uploadPassportFile(token, form);
                if (r.code !== 1) throw new Error(r.message);
                setRawText(r.data);
                setPhase("verify-passport");
            }

            else if (subPhase === "birth_certificate") {
                r = await uploadBirthCertificate(token, form);
                if (r.code !== 1) throw new Error(r.message);
                onNext("birth_certificate");
                return;
            }

            else if (subPhase === "green_card") {
                r = await uploadGreenCard(token, form);
                if (r.code !== 1) throw new Error(r.message);
                setRawText(r.data);
                setPhase("verify-gc");
            }

            else if (subPhase === "ead") {
                r = await uploadEADCard(token, form);
                if (r.code !== 1) throw new Error(r.message);
                setRawText(r.data);
                setPhase("verify-ead");
            }

            else if (step.step_type === "ssn_ocr") {
                r = await uploadSSNFile(token, form);
                if (r.code !== 1) throw new Error(r.message);
                setRawText(r.data);
                setPhase("verify-ssn");
            }

            else if (step.step_type === "driver_license_ocr") {
                r = await uploadDriverLicense(token, form);
                if (r.code !== 1) throw new Error(r.message);
                setRawText(r.data);
                setPhase("verify-license");
            }

            else if (step.step_type === "take_a_picture") {
                r = await updateProfilePicture(token, form);
                if (r.code !== 1) throw new Error(r.message);
                onNext();
                return;
            }

        } catch (err) {
            setError(err.message);
        }

        setLoading(false);
    };

    const handleSubmitFinal = async (payload) => {
        try {
            setLoading(true);

            let r;

            if (payload.type === "driver_license") {
                r = await updateDriverLicenseInformation(token, payload.data);
            }

            else if (payload.type === "passport") {
                r = await updatePassportInformation(token, payload.data);
            }

            else if (payload.type === "green_card") {
                r = await updateGreenCardInformation(token, payload.data);
            }

            else if (payload.type === "ead") {
                r = await updateEADInformation(token, payload.data);
            }

            else if (payload.type === "ssn") {
                r = await updateSSNInformation(token, payload.data);
            }

            if (r.code !== 1) throw new Error(r.message);

            console.log("subphase: ", subPhase)

            onNext(subPhase);

        } catch (err) {
            setError(err.message);
        }

        setLoading(false);
    };

    useEffect(() => {
        setPhase("choose");
        setSubPhase("");
        setPreview("");
        setRawText("");
        setFields({});
    }, [step]);

    if (phase === "choose" && step.step_type === "choose_citizen_document") {
        return <CitizenDocumentChooser onSelect={handleChoose} />;
    }

    if (phase === "choose" && step.step_type === "choose_noncitizen_status") {
        return <NonCitizenDocumentChooser onSelect={handleChoose} />;
    }

    if (step.step_type === "ssn_ocr" && phase === "choose") {
        return (
            <div style={{ textAlign: "center" }}>
                <h1 className="text-dark mb-4 fw-semibold">{step.step_title}</h1>
                <h6 className="text-dark mb-4" style={{ whiteSpace: "pre-line" }}>{step.step_description}</h6>

                <StepCompleteActionButton
                    label="Upload Your SSN"
                    onClick={() =>
                        setPhase("camera")
                    }
                />
            </div>
        );
    }

    if (step.step_type === "driver_license_ocr" && phase === "choose") {
        return (
            <div style={{ textAlign: "center" }}>
                <h1 className="text-dark mb-4 fw-semibold">{step.step_title}</h1>
                <h6 className="text-dark mb-4" style={{ whiteSpace: "pre-line" }}>{step.step_description}</h6>

                <StepCompleteActionButton
                    label="Upload Your Driver License"
                    onClick={() =>
                        setPhase("camera")
                    }
                />
            </div>
        );
    }

    console.log("stepout if: ", step.step_type)
    console.log("phase out if: ", phase)
    console.log("cond: ", step.step_type === "take_a_picture" && phase === "choose")

    if (step.step_type === "take_a_picture" && phase === "choose") {
        console.log("stepin if: ", step)
        console.log("phase in if: ", phase)
        return (
            <div style={{ textAlign: "center" }}>
                <h1 className="text-dark mb-4 fw-semibold">{step.step_title}</h1>
                <h6 className="text-dark mb-4" style={{ whiteSpace: "pre-line" }}>{step.step_description}</h6>

                <StepCompleteActionButton
                    label="Take A Picture"
                    onClick={() =>
                        setPhase("camera")
                    }
                />
            </div>
        );
    }

    if (phase === "camera") {
        return (
            <CameraController
                videoRef={videoRef}
                canvasRef={canvasRef}
                preview={preview}
                onPhotoTaken={handleTakePhoto}
                onUpload={handleUpload}
                onRetake={handleRetake}
            />
        );
    }

    if (phase === "verify-license") {
        return (
            <DriverLicenseFlow
                preview={preview}
                rawText={rawText}
                email={email}
                onRetake={handleRetake}
                onSubmit={handleSubmitFinal}
                fields={fields}
                setFields={setFields}
                error={error}
                loading={loading}
            />
        );
    }

    if (phase === "verify-passport") {
        return (
            <PassportFlow
                preview={preview}
                rawText={rawText}
                email={email}
                onRetake={handleRetake}
                onSubmit={handleSubmitFinal}
                fields={fields}
                setFields={setFields}
                error={error}
                loading={loading}
            />
        );
    }

    if (phase === "verify-gc") {
        return (
            <GreenCardFlow
                preview={preview}
                rawText={rawText}
                email={email}
                onRetake={handleRetake}
                onSubmit={handleSubmitFinal}
                fields={fields}
                setFields={setFields}
                error={error}
                loading={loading}
            />
        );
    }

    if (phase === "verify-ead") {
        return (
            <EadFlow
                preview={preview}
                rawText={rawText}
                email={email}
                onRetake={handleRetake}
                onSubmit={handleSubmitFinal}
                fields={fields}
                setFields={setFields}
                error={error}
                loading={loading}
            />
        );
    }

    if (phase === "verify-ssn") {
        return (
            <SsnFlow
                preview={preview}
                rawText={rawText}
                email={email}
                onRetake={handleRetake}
                onSubmit={handleSubmitFinal}
                fields={fields}
                setFields={setFields}
                error={error}
                loading={loading}
            />
        );
    }

    return null;
};

export default DynamicCameraOcrController;